import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, PencilLine, Send, Unlock, Trash2 } from "lucide-react";
import { api, errMsg, eur, dt, today, download, ALIQUOTE, NATURE } from "@/lib/api";
import { Field, Empty } from "@/components/Common";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer } from "lucide-react";
import { CorrImport } from "@/components/CorrImport";
import { DateField } from "@/components/DateField";

const STATO = { aperta: ["Aperta", "bg-amber-50 text-amber-700"], chiusa: ["Da trasmettere", "bg-sky-50 text-sky-700"], trasmessa: ["Trasmessa", "bg-emerald-50 text-emerald-700"] };
const EMPTY_MAN = () => ({ data: today(), contanti: 0, elettronico: 0, note: "", righe: ALIQUOTE.map((a) => ({ aliquota: a, natura: a ? "" : "N2.2", lordo: 0 })) });

const ManualeDialog = ({ value, setValue, onSaved }) => {
  const save = async () => {
    try {
      await api.post("/corrispettivi/manuale", { ...value, contanti: Number(value.contanti || 0), elettronico: Number(value.elettronico || 0), righe: value.righe.map((r) => ({ ...r, lordo: Number(r.lordo || 0) })) });
      toast.success("Corrispettivi della giornata salvati"); setValue(null); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && setValue(null)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="manual-day-dialog">
        <DialogHeader><DialogTitle>Inserimento manuale corrispettivi</DialogTitle></DialogHeader>
        {value && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Data" className="sm:col-span-2"><DateField testid="manual-day-date" value={value.data} onChange={(v) => setValue({ ...value, data: v })} /></Field>
            {value.righe.map((r, i) => (
              <Field key={i} label={r.aliquota ? `Incassato IVA ${r.aliquota}% (lordo €)` : `Incassato ${NATURE[r.natura]?.split(" ")[0] || r.natura} non imponibile/esente (€)`}>
                <Input type="number" step="0.01" data-testid={`manual-day-amount-${r.aliquota}`} value={r.lordo} onChange={(e) => setValue({ ...value, righe: value.righe.map((x, j) => (j === i ? { ...x, lordo: e.target.value } : x)) })} />
              </Field>
            ))}
            <Field label="di cui contanti €"><Input type="number" step="0.01" data-testid="manual-day-cash" value={value.contanti} onChange={(e) => setValue({ ...value, contanti: e.target.value })} /></Field>
            <Field label="di cui elettronico €"><Input type="number" step="0.01" data-testid="manual-day-card" value={value.elettronico} onChange={(e) => setValue({ ...value, elettronico: e.target.value })} /></Field>
            <Field label="Note" className="sm:col-span-2"><Input data-testid="manual-day-note" value={value.note} onChange={(e) => setValue({ ...value, note: e.target.value })} /></Field>
          </div>
        )}
        <DialogFooter><Button data-testid="manual-day-save" onClick={save}>Salva giornata</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TrasmessoDialog = ({ day, setDay, onSaved }) => {
  const [prot, setProt] = useState("");
  const save = async () => {
    try { await api.post(`/corrispettivi/giornate/${day}/trasmesso`, { protocollo: prot }); toast.success("Giornata segnata come trasmessa"); setDay(null); setProt(""); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && setDay(null)}>
      <DialogContent data-testid="transmit-dialog">
        <DialogHeader><DialogTitle>Trasmissione del {dt(day)}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Trasmetti i corrispettivi dal registratore telematico oppure dal portale Fatture e Corrispettivi dell'Agenzia delle Entrate, poi riporta qui il numero di protocollo della ricevuta.</p>
        <Field label="Protocollo / ricevuta"><Input data-testid="transmit-protocol-input" value={prot} onChange={(e) => setProt(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="transmit-save" disabled={!prot.trim()} onClick={save}>Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Giornate = ({ write }) => {
  const [mese, setMese] = useState(today().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [man, setMan] = useState(null);
  const [tx, setTx] = useState(null);
  const load = useCallback(() => { api.get("/corrispettivi/giornate", { params: { mese } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [mese]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const elimina = (g) => {
    if (g.stato === "trasmessa" && window.prompt(`La giornata del ${dt(g.data)} risulta GIÀ TRASMESSA (protocollo ${g.protocollo}). Eliminandola sparirà dal registro anche se l'Agenzia l'ha ricevuta. Scrivi ELIMINA per confermare.`) !== "ELIMINA") return;
    if (g.stato === "aperta") {
      if (window.confirm(`Eliminare la giornata del ${dt(g.data)} e tutte le ${g.documenti} vendite registrate in cassa?`)) act(() => api.delete(`/corrispettivi/giornate/${g.data}/elimina`, { params: { vendite: true } }), "Giornata eliminata");
      return;
    }
    if (g.stato !== "trasmessa" && !window.confirm(`Eliminare definitivamente la chiusura del ${dt(g.data)}?`)) return;
    const vendite = g.origine === "cassa" && window.confirm(`Eliminare anche le ${g.documenti} vendite registrate in cassa quel giorno?\nOK = elimina anche le vendite · Annulla = la giornata tornerà "aperta"`);
    act(() => api.delete(`/corrispettivi/giornate/${g.data}/elimina`, { params: { vendite } }), "Chiusura eliminata");
  };
  return (
    <div className="space-y-4">
      {write && <CorrImport onDone={load} />}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Mese"><Input type="month" data-testid="days-month-input" className="w-44 bg-white" value={mese} onChange={(e) => setMese(e.target.value)} /></Field>
        <div className="ml-auto flex flex-wrap gap-2">
          {write && <Button variant="outline" className="bg-white" data-testid="manual-day-button" onClick={() => setMan(EMPTY_MAN())}><PencilLine className="mr-2 h-4 w-4" />Inserimento manuale</Button>}
          <Button variant="outline" className="bg-white" data-testid="print-register-button" onClick={() => download("/corrispettivi/registro.pdf", { mese }).catch((e) => toast.error(e.message))}><Printer className="mr-2 h-4 w-4" />Registro corrispettivi PDF</Button>
        </div>
      </div>
      {rows.length === 0 ? <Empty text="Nessun corrispettivo nel mese" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="days-table">
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Data</TableHead><TableHead>Origine</TableHead><TableHead>Per aliquota</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((g) => (
                <TableRow key={g.data} data-testid={`day-row-${g.data}`}>
                  <TableCell className="font-mono text-sm">{dt(g.data)}</TableCell>
                  <TableCell className="text-sm">{g.origine === "cassa" ? `Cassa (${g.documenti} vendite)` : g.origine === "importata" ? "Importata" : "Manuale"}</TableCell>
                  <TableCell className="text-xs">{g.riepilogo.map((r) => `${r.aliquota ? `${r.aliquota}%` : r.natura}: ${eur(r.lordo)}`).join(" · ")}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{eur(g.totale)}</TableCell>
                  <TableCell><span className={`rounded px-2 py-0.5 text-xs ${STATO[g.stato][1]}`} data-testid={`day-status-${g.data}`}>{STATO[g.stato][0]}{g.protocollo ? ` · ${g.protocollo}` : ""}</span></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {write && g.stato === "aperta" && <Button size="sm" variant="outline" data-testid={`day-close-${g.data}`} onClick={() => act(() => api.post(`/corrispettivi/chiusura/${g.data}`), "Giornata chiusa")}><Lock className="mr-1 h-4 w-4" />Chiudi</Button>}
                    {write && <Button size="sm" variant="outline" className="ml-1 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`day-delete-${g.data}`} onClick={() => elimina(g)}><Trash2 className="mr-1 h-4 w-4" />Elimina</Button>}
                    {write && g.stato === "chiusa" && <>
                      <Button size="sm" variant="outline" data-testid={`day-transmit-${g.data}`} onClick={() => setTx(g.data)}><Send className="mr-1 h-4 w-4" />Segna trasmessa</Button>
                      <Button size="sm" variant="ghost" data-testid={`day-reopen-${g.data}`} onClick={() => act(() => api.delete(`/corrispettivi/giornate/${g.data}`), "Giornata riaperta")}><Unlock className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ManualeDialog value={man} setValue={setMan} onSaved={load} />
      <TrasmessoDialog day={tx} setDay={setTx} onSaved={load} />
    </div>
  );
};
